import {
  PermissionActionCategory,
  type Permission,
  type PermissionArea,
  PermissionType,
} from '@repo/api-gw-sdk';

export type PermissionDescriptor = Permission & {
  group?: string;
};

export const ManageBackupSettingsGroup = 'Manage backup settings';

const SearchWorkspaceDataGroup = 'Search and explore workspace data';
const RestoreWorkspaceDataGroup = 'Restore workspace data';

const permissions: Record<PermissionType, { group?: string }> = {
  [PermissionType.GlobalSettingsView]: {},
  [PermissionType.VaultAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.CostExplorerView]: {},
  [PermissionType.AuditLogsView]: {},
  [PermissionType.UserAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.SamlGroupAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsRotateSecret]: {
    group: 'Access management',
  },
  [PermissionType.CustomRolesManage]: {
    group: 'Access management',
  },
  [PermissionType.OauthAppsManage]: {
    group: 'Integrations',
  },
  [PermissionType.NotificationPoliciesManage]: {
    group: 'Integrations',
  },
  [PermissionType.PermissionIdUnspecified]: {},
  [PermissionType.DashboardView]: {},
  [PermissionType.SaasDashboardView]: {},
  [PermissionType.DashboardManage]: {},
  [PermissionType.AiAssistantUse]: {},
  [PermissionType.InventoryView]: {},
  [PermissionType.SaasInventoryView]: {},
  [PermissionType.JobsView]: {},
  [PermissionType.SaasJobsView]: {},
  [PermissionType.BackupSettingsView]: {
    group: 'Backup settings',
  },
  [PermissionType.ThreatDetectionCenterView]: {},
  [PermissionType.StorageGatewayView]: {},
  [PermissionType.DatabaseExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.FileExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.SaasMailSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupDataSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.ThreatDetectionExclusionManage]: {},
  [PermissionType.FileExplorerDelete]: {},
  [PermissionType.StorageGatewayDataDownload]: {},
  [PermissionType.StorageGatewayMetadataView]: {},
  [PermissionType.DataCatalogView]: {},
  [PermissionType.DataCatalogQuery]: {},
  [PermissionType.DataCatalogPermissionsManage]: {
    group: 'Data catalog access control',
  },
  [PermissionType.DataCatalogTagsManage]: {
    group: 'Data catalog access control',
  },
  [PermissionType.SnapshotsTakeOrConvert]: {},
  [PermissionType.SaasSnapshotsTake]: {},
  [PermissionType.ResourcesRestore]: {},
  [PermissionType.ConfigurationValidate]: {},
  [PermissionType.SaasConfigurationValidate]: {},
  [PermissionType.DiscoveryTrigger]: {},
  [PermissionType.SaasDiscoveryTrigger]: {},
  [PermissionType.FilesRestore]: {
    group: 'Restore granular data',
  },
  [PermissionType.SaasMailRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SourceAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.SaasSourceAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.RestoreAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.DiscoveryConfigurationView]: {
    group: 'Backup settings',
  },
  [PermissionType.VaultsView]: {
    group: 'Backup settings',
  },
  [PermissionType.BackupPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.SaasBackupPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.ClassificationPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.ObjectFiltersView]: {
    group: 'Backup settings',
  },
  [PermissionType.BackupConfigurationView]: {
    group: 'Backup settings',
  },
  [PermissionType.ZeroEtlPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.RestoreTemplatesView]: {
    group: 'Backup settings',
  },
  [PermissionType.DatabasesQuery]: {
    group: 'Restore granular data',
  },
  [PermissionType.DataClassesOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.EnvironmentsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.DetectedAppsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.SensitivityLevelOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.NetworkClassificationOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.ControlViolationsMute]: {
    group: 'Resource exceptions',
  },
  [PermissionType.BackupExclusionManage]: {
    group: 'Resource exceptions',
  },
  [PermissionType.VaultsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasSourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RestoreAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DiscoveryConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ZeroEtlPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ControlsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RansomwareDetectionManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupScopeManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.StorageGatewayManage]: {
    group: 'Storage gateway',
  },
  [PermissionType.ActionApprovalRulesManage]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRulesView]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRequestsManage]: {},
  [PermissionType.MpaPoliciesManage]: {
    group: 'Access management',
  },
  [PermissionType.MpaPoliciesView]: {
    group: 'Access management',
  },
  [PermissionType.MpaRequestsManage]: {},
  [PermissionType.RestoreTemplatesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DatalakeView]: {
    group: 'Managed data lake',
  },
  [PermissionType.DatalakeOperate]: {
    group: 'Managed data lake',
  },
  [PermissionType.ComplianceView]: {
    group: 'Backup settings',
  },
  [PermissionType.ClassificationPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
};

export const Permissions = Object.fromEntries(
  Object.entries(permissions).filter(
    ([key]) => key !== PermissionType.PermissionIdUnspecified
  )
);

// The pre-families roles UI renders Global management as a fourth section beside the tiers. The
// API stopped expressing that — global management is an area — so the legacy bucket is a
// console-local vocabulary composed from areas + category.
export const GlobalManagementLegacyCategory = 'GLOBAL_MANAGEMENT';

type LegacyPermissionCategory =
  PermissionActionCategory | typeof GlobalManagementLegacyCategory;

const permissionCategories: Record<
  LegacyPermissionCategory,
  { title: string }
> = {
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_ADMINISTRATION]: {
    title: 'Administration',
  },
  [GlobalManagementLegacyCategory]: {
    title: 'Global management',
  },
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_OPERATION]: {
    title: 'Operation',
  },
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_VIEWING]: {
    title: 'Viewing',
  },
};

export const PermissionCategories = Object.fromEntries(
  Object.entries(permissionCategories)
);

export enum PermissionFamily {
  GlobalManagement = 'GLOBAL_MANAGEMENT',
  Cloud = 'CLOUD',
  Saas = 'SAAS',
  StorageGateway = 'STORAGE_GATEWAY',
}

const familyOrder = [
  PermissionFamily.GlobalManagement,
  PermissionFamily.Cloud,
  PermissionFamily.Saas,
  PermissionFamily.StorageGateway,
];

// The SDK enum and the local one share their string values, so mapping is a cast; the sort keeps
// the chips in a stable order regardless of how the server ordered the list.
export const getPermissionFamilies = (
  permission: PermissionDescriptor
): PermissionFamily[] =>
  (permission.areas ?? [])
    .map((area: PermissionArea) => area as string as PermissionFamily)
    .sort((a, b) => familyOrder.indexOf(a) - familyOrder.indexOf(b));

// A permission the gateway shares with another provider still has to reach that provider's
// surface, so only the gateway-exclusive ones follow the gateway's availability.
export const isStorageGatewayOnly = (permission: PermissionDescriptor) => {
  const families = getPermissionFamilies(permission);

  return (
    families.length > 0 &&
    families.every((family) => family === PermissionFamily.StorageGateway)
  );
};

export const getPermissionSection = (
  permission: PermissionDescriptor
): PermissionActionCategory => permission.category;

// The category chip of the pre-families roles UI: global management is its own section there,
// while the families UI renders it as a family and files the permission under its real section.
export const getLegacyPermissionCategory = (
  permission: PermissionDescriptor
): LegacyPermissionCategory =>
  getPermissionFamilies(permission).includes(PermissionFamily.GlobalManagement)
    ? GlobalManagementLegacyCategory
    : permission.category;
